import React from "react";
import { motion } from "framer-motion";
import { Box, Typography, Icon, useTheme } from "@mui/material";

interface EmptyStateProps {
  icon: string;
  title: string;
  message: string;
  height?: number | string;
  delay?: number;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  message,
  height = 300,
  delay = 0,
}) => {
  const theme = useTheme();

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay,
        ease: "easeOut",
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height,
        py: 4,
        px: 2,
        textAlign: "center",
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          duration: 0.5,
          delay: delay + 0.2,
          type: "spring",
          stiffness: 260,
          damping: 20,
        }}
      >
        <Icon
          style={{
            fontSize: 48,
            color: theme.palette.text.secondary,
            marginBottom: 16,
            opacity: 0.5,
          }}
        >
          {icon}
        </Icon>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.5,
          delay: delay + 0.4,
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          color="text.secondary"
          sx={{ fontWeight: 500 }}
        >
          {title}
        </Typography>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.5,
          delay: delay + 0.6,
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 300 }}
        >
          {message}
        </Typography>
      </motion.div>
    </Box>
  );
};
